// ============================================================
// WHAT A VEHICLE'S PLATE SAYS (plan body-map P6; the vehicle studies, 2026-10-09):
//
// - a callout per location (Hull, Turret, Motive, Rear) with its NAME and its tags only - no roll ranges (user):
//   its damage (Destroyed, Crippled), then the vehicle's conditions that live there (a destroyed weapon and a hit
//   crew on the Turret, Immobilised on the Motive, a damaged engine on the Rear, a breach on the Hull), then the
//   cover's AP where the cover protects it;
// - each FACING's armour at that side of the vehicle, the facing in play lit. `rear` is both a location (the
//   callout) and a facing (the label) - kept apart: the two never share a record.
//
// Pure. Zero React dependency.
// ============================================================

import { getBodyConfig, isLocCoveredForBody } from '../bodyConfig';
import { condType } from '../conditions';
import type { VehicleFacing } from '../vehicleFacing';
import { damageOf, locationMarks, type LocationMark } from './plateModel';
import { piecePoints } from './figureArt';
import type { Blow } from './vehicleArt';

type Location = 'hull' | 'turret' | 'motive' | 'rear';
type Point = readonly [number, number];
type Tone = 'destroyed' | 'crippled' | 'calm' | 'cover';

/** What the callouts read off the art. */
interface CalloutArt {
  anchors: Readonly<Record<Location, Point>>;
  rows: Readonly<Record<Location, number>>;
  sides: Readonly<Record<Location, 'L' | 'R'>>;
}

export interface VehicleCallout {
  id: Location;
  name: string;
  side: 'L' | 'R';
  row: number;
  anchor: Point;
  /** Its Crippled / Destroyed marks (the lines' look). */
  marks: LocationMark[];
  tags: { text: string; tone: Tone }[];
}

/** The vehicle conditions each location carries, in the canvas's order. Immobilised reads calm: it is not a wound. */
const LOCATION_CONDITIONS: Record<Location, [code: string, text: string, tone: Tone][]> = {
  hull: [['HullBr', 'HULL BREACH', 'destroyed']],
  turret: [
    ['WepDest', 'WEAPON DESTROYED', 'destroyed'],
    ['CrewHit', 'CREW HIT', 'destroyed'],
  ],
  motive: [['Immob', 'IMMOBILISED', 'calm']],
  rear: [['EngDmg', 'ENGINE DAMAGED', 'destroyed']],
};

const VEHICLE = getBodyConfig('vehicle');

function tagsOf(id: Location, marks: LocationMark[], held: Set<string>): VehicleCallout['tags'] {
  const hurt = damageOf(marks);
  const damage: VehicleCallout['tags'] = hurt ? [{ text: hurt.toUpperCase(), tone: hurt }] : [];
  const conditions = LOCATION_CONDITIONS[id].filter(([code]) => held.has(code));
  return [...damage, ...conditions.map(([, text, tone]) => ({ text, tone }))];
}

/** One callout per vehicle location, in the body's order. */
export function vehicleCallouts(
  art: CalloutArt,
  conditions: string[],
  cover?: { level: string; ap: number },
): VehicleCallout[] {
  const marks = locationMarks(conditions, VEHICLE);
  const held = new Set(conditions.map(condType));
  // The cover's AP on the locations it protects, as the old map added it to the hull's numeral.
  const covered = (id: string) => !!cover && cover.ap > 0 && isLocCoveredForBody(cover.level, id, VEHICLE);
  const coverTag = { text: `COVER +${cover?.ap ?? 0}`, tone: 'cover' as const };
  return VEHICLE.locations.map(({ id, name }) => {
    const loc = id as Location;
    return {
      id: loc,
      name,
      side: art.sides[loc],
      row: art.rows[loc],
      anchor: art.anchors[loc],
      marks: marks[id] ?? [],
      tags: [...tagsOf(loc, marks[id] ?? [], held), ...(covered(id) ? [coverTag] : [])],
    };
  });
}

export interface FacingLabel {
  id: 'front' | 'rear' | 'left' | 'right';
  word: 'FRONT' | 'REAR' | 'SIDE';
  armour: number;
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
  /** The facing in play: the side an attack strikes. */
  lit: boolean;
}

type Spot = Pick<FacingLabel, 'x' | 'y' | 'anchor'>;

/**
 * Where each label sits round the 2123 x 3000 frame. From above the front is at the top; seen from its front (the
 * walker) the front faces you - below - its rear is behind it, above, and its left side is on your right.
 */
const SPOTS: Record<'top' | 'front', Record<FacingLabel['id'], Spot>> = {
  top: {
    front: { x: 1061, y: -260, anchor: 'middle' },
    rear: { x: 1061, y: 3110, anchor: 'middle' },
    left: { x: 330, y: 1490, anchor: 'end' },
    right: { x: 1792, y: 1490, anchor: 'start' },
  },
  front: {
    front: { x: 1061, y: 3110, anchor: 'middle' },
    rear: { x: 1061, y: -260, anchor: 'middle' },
    left: { x: 1792, y: 1490, anchor: 'start' },
    right: { x: 330, y: 1490, anchor: 'end' },
  },
};

const LABELS: [FacingLabel['id'], FacingLabel['word'], VehicleFacing][] = [
  ['front', 'FRONT', 'front'],
  ['rear', 'REAR', 'rear'],
  ['left', 'SIDE', 'side'],
  ['right', 'SIDE', 'side'],
];

/** Each facing's armour at that side of the vehicle, the facing in play lit. */
export function vehicleFacings(
  view: 'top' | 'front',
  armour: Partial<Record<VehicleFacing, number>>,
  inPlay: VehicleFacing,
): FacingLabel[] {
  return LABELS.map(([id, word, facing]) => ({
    id,
    word,
    armour: armour[facing] ?? 0,
    ...SPOTS[view][id],
    lit: facing === inPlay,
  }));
}

/** Where one piece of a location is thrown when the vehicle explodes: frame px and degrees. */
export interface PieceThrow {
  tx: number;
  ty: number;
  rot: number;
}

/** A piece's centre, from its polygon ("x,y x,y ..."). */
function centreOf(piece: string): [number, number] {
  const pts = piecePoints(piece);
  return [pts.reduce((a, [x]) => a + x, 0) / pts.length, pts.reduce((a, [, y]) => a + y, 0) / pts.length];
}

/**
 * The blast on one piece (canvas ruling 2026-10-09): a location drawn in one piece takes its blow as given; one drawn
 * in several (two tracks, two legs, two wheels) throws each `spread` px outward from the vehicle's own middle (its
 * drawing's box), turning away from it - moving whole pieces, never stretching or cutting them.
 */
export function pieceThrow(
  blow: Blow,
  pieces: number,
  piece: string | null,
  middle: readonly [number, number],
): PieceThrow {
  const base = { tx: blow.tx ?? 0, ty: blow.ty ?? 0, rot: blow.rot ?? 0 };
  if (pieces < 2 || !blow.spread || piece === null) return base;
  const [cx, cy] = centreOf(piece);
  const [dx, dy] = [cx - middle[0], cy - middle[1]];
  const len = Math.hypot(dx, dy) || 1;
  const away = Math.sign(dx || dy) || 1;
  return { tx: base.tx + (dx / len) * blow.spread, ty: base.ty + (dy / len) * blow.spread, rot: base.rot * away };
}

/** A location's pieces as the blast throws them: each drawn piece on its own, the Hull (the rest) as one (null). */
export const piecesOf = (art: { regions: Record<string, string[]> }, id: string): (string | null)[] =>
  id === 'hull' ? [null] : art.regions[id];
